import { forwardRef, ReactNode } from 'react';
import * as RadixSwitch from '@radix-ui/react-switch';
import { cn } from '../lib/cn';

interface SwitchProps
  extends Omit<React.ComponentPropsWithoutRef<typeof RadixSwitch.Root>, 'children'> {
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
}

export const Switch = forwardRef<
  React.ElementRef<typeof RadixSwitch.Root>,
  SwitchProps
>(function Switch({ label, description, className, id, ...rest }, ref) {
  const root = (
    <RadixSwitch.Root
      ref={ref}
      id={id}
      className={cn(
        'peer relative h-6 w-11 shrink-0 cursor-pointer rounded-full border border-transparent bg-border-strong',
        'transition-colors duration-fast ease-enter',
        'data-[state=checked]:bg-primary',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      <RadixSwitch.Thumb
        className={cn(
          'block h-5 w-5 rounded-full bg-surface shadow-sm',
          'transition-transform duration-fast ease-enter',
          'translate-x-0.5 data-[state=checked]:translate-x-[1.375rem]',
        )}
      />
    </RadixSwitch.Root>
  );

  if (!label && !description) return root;

  return (
    <label htmlFor={id} className="flex cursor-pointer items-start justify-between gap-4 select-none">
      <div className="min-w-0">
        {label ? (
          <span className="block text-base text-ink leading-ui">{label}</span>
        ) : null}
        {description ? (
          <span className="mt-0.5 block text-sm text-ink-secondary">
            {description}
          </span>
        ) : null}
      </div>
      {root}
    </label>
  );
});
